// the map: places nearby with their rating, reviews and parking, and routes through a few stops.
// shared by the server (which asks google) and the app (which draws it)

export interface LatLng {
  lat: number;
  lng: number;
}

// halifax, where fractal is. the map opens here until "near me" finds somewhere else
export const MAP_HOME: LatLng & { label: string } = { lat: 53.73, lng: -1.9, label: "halifax" };

export interface MapPlace {
  id: string;
  name: string;
  address: string;
  location: LatLng;
  // what google calls it, "italian restaurant", "pub"
  kind: string;
  // 0 when nobody has rated it yet
  rating: number;
  reviews: number;
  // null when google doesn't say. an empty list means it says there's none
  parking: string[] | null;
  openNow: boolean | null;
  mapsUrl: string;
  // from where the search was centred, in metres
  distance: number;
}

export interface SavedPlace extends MapPlace {
  savedAt: string;
  note: string;
  // a pin someone dropped themselves, in one of PIN_COLOURS. empty for places from google
  colour?: string;
}

// pins people drop are their own places, told apart by this id prefix
export const PIN_PREFIX = "pin:";
export const PIN_COLOURS = ["chi", "violet", "amber", "green", "red"];

export function isPin(place: { id: string }): boolean {
  return place.id.startsWith(PIN_PREFIX);
}

export function pointLink(at: LatLng): string {
  return `https://www.google.com/maps/search/?api=1&query=${at.lat.toFixed(6)},${at.lng.toFixed(6)}`;
}

export type TravelMode = "drive" | "walk" | "cycle" | "transit";

export interface RouteStop {
  label: string;
  // a google place, a point, or text google looks up. the first one set wins
  placeId?: string;
  location?: LatLng;
  address?: string;
}

export interface RouteLeg {
  from: string;
  to: string;
  distance: number;
  duration: number;
}

export interface RoutePlan {
  distance: number;
  duration: number;
  polyline: string;
  legs: RouteLeg[];
  // the stops in the order driven, when google picked the best order
  stops: RouteStop[];
  warnings: string[];
}

export interface SavedRoute {
  id: string;
  name: string;
  stops: RouteStop[];
  mode: TravelMode;
  savedAt: string;
}

export interface MapsState {
  places: SavedPlace[];
  routes: SavedRoute[];
}

export interface MapsConfig {
  // the server can search and plan (GOOGLE_MAPS_API_KEY or the key in setup)
  ready: boolean;
  // the key the map tiles load with, empty when there's none
  browserKey: string;
}

export interface NearbyAsk {
  centre: LatLng;
  // a google place type from MAP_CATEGORIES, or empty when searching by words
  category?: string;
  // words, "sushi", "art supplies", an address
  query?: string;
  radius?: number;
}

export const MAP_CATEGORIES = [
  { id: "restaurant", label: "food" },
  { id: "cafe", label: "cafés" },
  { id: "bar", label: "pubs and bars" },
  { id: "fast_food_restaurant", label: "takeaway" },
  { id: "supermarket", label: "shops" },
  { id: "gas_station", label: "fuel" },
  { id: "parking", label: "parking" },
  { id: "pharmacy", label: "pharmacy" },
  { id: "park", label: "parks" },
  { id: "tourist_attraction", label: "things to see" },
  { id: "movie_theater", label: "cinema" },
  { id: "electric_vehicle_charging_station", label: "ev charging" }
];

export const MAP_RADII = [1000, 3000, 10000];

export const TRAVEL_MODES: Array<{ id: TravelMode; label: string; google: string; link: string }> = [
  { id: "drive", label: "drive", google: "DRIVE", link: "driving" },
  { id: "walk", label: "walk", google: "WALK", link: "walking" },
  { id: "cycle", label: "cycle", google: "BICYCLE", link: "bicycling" },
  { id: "transit", label: "bus/train", google: "TRANSIT", link: "transit" }
];

// google's parking switches, in the words shown on a card
export const PARKING_LABELS: Record<string, string> = {
  freeParkingLot: "free car park",
  paidParkingLot: "paid car park",
  freeStreetParking: "free street",
  paidStreetParking: "paid street",
  freeGarageParking: "free garage",
  paidGarageParking: "paid garage",
  valetParking: "valet"
};

export function parkingText(parking: string[] | null): string {
  if (parking === null) return "no parking info";
  if (!parking.length) return "no parking listed";
  return parking.join(", ");
}

export function distanceText(metres: number): string {
  if (!metres) return "";
  // the uk drives in miles
  const miles = metres / 1609.34;
  return miles < 0.2 ? `${Math.round(metres / 10) * 10} m` : `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
}

export function durationText(seconds: number): string {
  const minutes = Math.max(1, Math.round(seconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

export function metresBetween(a: LatLng, b: LatLng): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return Math.round(6371000 * 2 * Math.asin(Math.sqrt(h)));
}

// how a stop is written in a google maps link
export function stopText(stop: RouteStop): string {
  if (stop.location) return `${stop.location.lat},${stop.location.lng}`;
  return stop.address || stop.label;
}

// opens the route in google maps itself, for turn by turn on the phone
export function directionsLink(stops: RouteStop[], mode: TravelMode): string {
  if (stops.length < 2) return "";
  const params = new URLSearchParams({ api: "1", origin: stopText(stops[0]), destination: stopText(stops[stops.length - 1]) });
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (first.placeId) params.set("origin_place_id", first.placeId);
  if (last.placeId) params.set("destination_place_id", last.placeId);
  const middle = stops.slice(1, -1);
  if (middle.length) params.set("waypoints", middle.map(stopText).join("|"));
  if (middle.length && middle.every((stop) => stop.placeId)) params.set("waypoint_place_ids", middle.map((stop) => stop.placeId).join("|"));
  params.set("travelmode", TRAVEL_MODES.find((item) => item.id === mode)?.link || "driving");
  return `https://www.google.com/maps/dir/?${params}`;
}
